/* eslint-disable ember/no-classic-components */
import Component from "@ember/component";
import { computed } from "@ember/object";
import { cancel, next } from "@ember/runloop";
import { trustHTML } from "@ember/template";
import { tagName } from "@ember-decorators/component";
import { on } from "@ember-decorators/object";
import DButton from "discourse/components/d-button";
import { DELETE_REPLIES_TYPE } from "discourse/components/modal/edit-topic-timer";
import { isTesting } from "discourse/lib/environment";
import { iconHTML } from "discourse/lib/icon-library";
import discourseLater from "discourse/lib/later";
import Category from "discourse/models/category";
import { i18n } from "discourse-i18n";

@tagName("")
export default class TopicTimerInfo extends Component {
  clockIcon = trustHTML(`${iconHTML("far-clock")}`);
  trashLabel = i18n("post.controls.remove_timer");

  title = null;
  notice = null;
  showTopicTimer = null;
  showTopicTimerModal = null;
  removeTopicTimer = null;
  _delayedRerender = null;

  @on("didReceiveAttrs")
  setupRenderer() {
    this.renderTopicTimer();
  }

  @on("willDestroyElement")
  cancelDelayedRenderer() {
    if (this._delayedRerender) {
      cancel(this._delayedRerender);
    }
  }

  @computed
  get canModifyTimer() {
    return this.currentUser && this.currentUser.get("canManageTopic");
  }

  @computed("canModifyTimer", "removeTopicTimer")
  get showTrashCan() {
    return this.canModifyTimer && this.removeTopicTimer;
  }

  @computed("canModifyTimer", "showTopicTimerModal")
  get showEdit() {
    return this.canModifyTimer && this.showTopicTimerModal;
  }

  additionalOpts() {
    return {};
  }

  renderTopicTimer() {
    const isDeleteRepliesType = this.statusType === DELETE_REPLIES_TYPE;

    if (
      !isDeleteRepliesType &&
      !this.basedOnLastPost &&
      (!this.executeAt || this.executeAt < moment())
    ) {
      this.set("showTopicTimer", null);
      return;
    }

    if (this.isDestroyed) {
      return;
    }

    const topicStatus = this.topicClosed ? "close" : "open";
    const topicStatusKnown = this.topicClosed !== undefined;
    const topicStatusUpdate = this.statusUpdate !== undefined;
    if (topicStatusKnown && topicStatus === this.statusType) {
      if (!topicStatusUpdate) {
        return;
      }

      // The topic status has just been toggled, so we can hide the timer info.
      this.set("showTopicTimer", null);
      // The timer has already been removed on the back end. The front end is not aware of the change yet.
      // TODO: next() is a hack, to-be-removed
      next(() => this.set("executeAt", null));
      return;
    }

    const statusUpdateAt = moment(this.executeAt);
    const duration = moment.duration(statusUpdateAt - moment());
    const minutesLeft = duration.asMinutes();
    if (minutesLeft > 0 || isDeleteRepliesType || this.basedOnLastPost) {
      // We don't want to display a notice before a topic timer time has been set
      if (!this.executeAt) {
        return;
      }

      let durationMinutes = parseInt(this.durationMinutes, 10) || 0;

      let options = {
        timeLeft: duration.humanize(true),
        duration: moment
          .duration(durationMinutes, "minutes")
          .humanize({ s: 60, m: 60, h: 24 }),
      };

      const categoryId = this.categoryId;
      if (categoryId) {
        const category = Category.findById(categoryId);

        if (category) {
          options = Object.assign(
            {
              categoryName: category.get("slug"),
              categoryUrl: category.get("url"),
            },
            options
          );
        }
      }

      options = Object.assign(options, this.additionalOpts());
      this.setProperties({
        title: trustHTML(`${moment(this.executeAt).format("LLLL")}`),
        notice: trustHTML(`${i18n(this._noticeKey(), options)}`),
        showTopicTimer: true,
      });

      // TODO Sam: concerned this can cause a heavy rerender loop
      if (!isTesting()) {
        this._delayedRerender = discourseLater(() => {
          this.renderTopicTimer();
        }, this.rerenderDelay(minutesLeft));
      }
    } else {
      this.set("showTopicTimer", null);
    }
  }

  rerenderDelay(minutesLeft) {
    if (minutesLeft > 2160) {
      return 12 * 60 * 60000;
    } else if (minutesLeft > 1410) {
      return 60 * 60000;
    } else if (minutesLeft > 90) {
      return 30 * 60000;
    } else if (minutesLeft > 2) {
      return 60000;
    }

    return 1000;
  }

  _noticeKey() {
    let statusType = this.statusType;
    if (statusType === "silent_close") {
      statusType = "close";
    }
    if (this.basedOnLastPost && statusType === "close") {
      statusType = "close_after_last_post";
    }
    if (this.basedOnLastPost && statusType === "delete") {
      statusType = "delete_after_last_post";
    }

    return `topic.status_update_notice.auto_${statusType}`;
  }

  <template>
    <div class="topic-timer-info" ...attributes>
      {{#if this.showTopicTimer}}
        <h3 class="topic-timer-heading">
          <span title={{this.title}}>
            {{this.clockIcon}}
            {{this.notice}}
          </span>
          <div class="topic-timer-modify">
            {{#if this.showEdit}}
              <DButton
                @title="post.controls.edit_timer"
                @icon="pencil"
                @action={{this.showTopicTimerModal}}
                class="topic-timer-edit no-text"
              />
            {{/if}}
            {{#if this.showTrashCan}}
              <DButton
                @title="post.controls.remove_timer"
                @icon="trash-can"
                @action={{this.removeTopicTimer}}
                class="topic-timer-remove no-text"
              />
            {{/if}}
          </div>
        </h3>
      {{/if}}
    </div>
  </template>
}
